import { frameStyles, scrollerStyles } from '@components/Customization/PdfPreview/PdfPreview.styles';
import { alpha, type Theme } from '@mui/material/styles';
import { A4, palette, pt } from './print';
import { freePage, typeStyles } from './print.styles';

/**
 * The canvas around what is printed: the desk, the sheets as objects on it, and the marks of
 * the editor on them (what is selected, where a drag would land, what an issue is about).
 * How the print itself looks is `print.styles.ts`; nothing here is on the report.
 *
 * These marks are sized in pixels, not in points: a toolbar has to be a toolbar on a sheet of
 * any width.
 */

const accent = (strength: number) => (theme: Theme) => alpha(theme.palette.primary.main, strength);

/** The same frame and the same desk as the PDF's pages lie on, so the two tabs are one place. */
export const canvasFrameStyles = frameStyles;

export const canvasDeskStyles = { ...scrollerStyles, gap: 3 };

/** The sheets in one column, no wider than the paper itself is at a screen's scale. */
export const sheetColumnStyles = {
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  gap: 3,
  width: '100%',
  maxWidth: 794,
  mx: 'auto',
};

/** A sheet with the line over it that names it, and what is said under it. */
export const sheetGroupStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  minWidth: 0,
};

/** The line over a sheet: what the page is, and what can be done to it as a whole. */
export const sheetHeaderStyles = {
  alignItems: 'center',
  gap: 0.5,
  minHeight: 28,
  '& .MuiIconButton-root': { p: 0.5, fontSize: 16, color: 'text.secondary' },
};

/** What a sheet is: "Cover page 2", "Results". */
export const sheetTitleStyles = {
  px: 0.75,
  py: 0.25,
  fontSize: 13,
  fontWeight: 600,
  color: 'text.secondary',
};

/** The same as a button, for a sheet that is a page of the design and can be selected by its name. */
export const sheetNameStyles = {
  ...sheetTitleStyles,
  gap: 0.75,
  borderRadius: 1,
  '&:hover': { backgroundColor: 'action.hover' },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.light' },
  '&[aria-current]': { color: 'primary.light' },
};

/**
 * Cuts off what runs over a box's edge. `clip` and not `hidden`: a box that hides what runs
 * over can still be scrolled by code, and bringing a part of it into view would scroll the
 * page inside its sheet, with nothing to scroll it back by. A browser that does not know
 * `clip` hides.
 */
const clipStyles = {
  overflow: 'hidden',
  '@supports (overflow: clip)': { overflow: 'clip' },
};

/** Paper, at the width its column gives it. */
const paperStyles = {
  ...typeStyles,
  ...clipStyles,
  position: 'relative',
  width: '100%',
  backgroundColor: palette.paper,
  boxShadow: '0 1px 6px rgba(0, 0, 0, .45)',
  // Its text is a picture of the report, and is dragged by: nothing on it is for selecting.
  userSelect: 'none',
  WebkitTouchCallout: 'none',
};

/** A sheet that is one page of A4 whatever is on it, as the first page of the results is: what does not fit follows on pages the canvas does not show. */
export const sheetStyles = {
  ...paperStyles,
  aspectRatio: `${A4.width} / ${A4.height}`,
};

/**
 * The sheet of a cover or a closing page: A4, and taller where the page holds more than a
 * page of paper does. A block that was cut off at the sheet's edge could be neither seen nor
 * taken hold of, and went on being something to drop on, unseen, under the sheets around it.
 */
export const pageSheetStyles = {
  ...paperStyles,
  display: 'flex',
  flexDirection: 'column',
  minHeight: pt(A4.height),
};

export const navySheetStyles = { backgroundColor: palette.navy };

/** The page is what the inspector shows. */
export const sheetSelectedStyles = {
  outline: '2px solid',
  outlineColor: 'primary.main',
  outlineOffset: 2,
};

/** A drag is over the page, and a drop would land on it. */
export const sheetLitStyles = {
  outline: '2px solid',
  outlineColor: accent(0.6),
  outlineOffset: 2,
};

/** Something an issue is about: a page, a block, a part of the results. */
export const flaggedStyles = {
  outline: '2px dashed',
  outlineColor: 'warning.main',
  outlineOffset: 2,
};

/**
 * The page inside its margins, its blocks one under the other. Clicked outside a block, it is
 * the page that is meant. It fills the sheet, and makes it taller when its blocks need more.
 */
export const pageColumnStyles = {
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
  padding: `${pt(freePage.paddingY)} ${pt(freePage.paddingX)}`,
};

/** A page's blocks. They are never squeezed to fit: what does not fit makes the sheet taller. */
export const blockStackStyles = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  minWidth: 0,
};

/** A mark of the canvas across a sheet, from edge to edge: it is laid over the blocks and takes no press. */
const acrossSheetStyles = {
  position: 'absolute',
  left: pt(-freePage.paddingX),
  right: pt(-freePage.paddingX),
  zIndex: 1,
  pointerEvents: 'none',
};

/**
 * Where the report goes on to its next sheet, on a page that flows. About there: the report
 * starts each sheet under its margin, and takes a block that cannot be divided over whole.
 */
export const pageBreakStyles = {
  ...acrossSheetStyles,
  height: 0,
  borderTop: '2px dashed',
  borderColor: accent(0.7),
};

/** What the report cuts off, on a page that does not flow: still to be seen and moved here, and marked as not printed. */
export const cutOffStyles = {
  ...acrossSheetStyles,
  backgroundImage: 'repeating-linear-gradient(135deg, rgba(128, 128, 128, .5) 0 6px, rgba(128, 128, 128, .28) 6px 12px)',
};

/**
 * One block on its sheet. A column of its own, so that a block's margins are its own and add
 * up with its neighbours' as the renderer adds them, where a browser would fold them into
 * one. `manipulation` and not `none`: a finger that moves at once scrolls the canvas.
 */
export const blockFrameStyles = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  minWidth: 0,
  borderRadius: '2px',
  outline: '1px dashed transparent',
  outlineOffset: 3,
  cursor: 'pointer',
  touchAction: 'manipulation',
  '&:hover': { outlineColor: accent(0.55) },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light' },
};

/**
 * The block can be picked up by any part of it. Said twice over: the frame is a `role="button"`,
 * for which Bootstrap's base styles set the pointer, and they are loaded after these.
 */
export const blockGrabStyles = { '&&': { cursor: 'grab' } };

export const blockSelectedStyles = {
  zIndex: 2,
  outline: '2px solid',
  outlineColor: 'primary.main',
  '&:hover': { outlineColor: 'primary.main' },
};

/** The block is in the overlay under the pointer; what stays behind marks where it came from. */
export const blockLiftedStyles = { opacity: 0.35 };

/**
 * Where a drop would land: a line between two blocks. Laid over the page and never in it, so
 * that nothing moves while a drag is on.
 */
export const dropLineStyles = {
  position: 'absolute',
  left: -6,
  right: -6,
  top: -3,
  zIndex: 3,
  height: 3,
  borderRadius: 2,
  backgroundColor: 'primary.main',
  boxShadow: (theme: Theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.22)}`,
  pointerEvents: 'none',
};

/** Under the last block of a page. */
export const dropLineBelowStyles = { top: 'auto', bottom: -3 };

/**
 * The selected block's toolbar, standing on its top edge. It is part of the block: taken by
 * it, the block is dragged. At the block's right end, because it lies over the block above,
 * and a text starts at the left more often than it reaches the right.
 */
export const blockToolbarStyles = {
  position: 'absolute',
  right: -2,
  top: -32,
  zIndex: 4,
  alignItems: 'center',
  height: 26,
  maxWidth: 'calc(100% + 4px)',
  px: 0.25,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper',
  color: 'text.primary',
  boxShadow: '0 2px 8px rgba(0, 0, 0, .35)',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  '& i': { fontSize: 15 },
  '& .MuiIconButton-root': { p: 0.375, fontSize: 15, color: 'text.secondary' },
};

/**
 * On a page's first block, which has only the page's margin over it: on a narrow sheet that
 * is less than the toolbar is tall, and the toolbar comes down onto the block rather than be
 * cut by the sheet's edge.
 */
export const firstBlockToolbarStyles = { top: `max(-32px, ${pt(-freePage.paddingY)})` };

/**
 * Room over the selected block for its toolbar when the canvas is taken to the block. The
 * toolbar stands on the block's top edge, outside its box: a block that is scrolled to from
 * below would come to rest with its toolbar cut off by the canvas's upper edge.
 */
export const toolbarRoomStyles = { scrollMarginTop: 40 };

/** Always to be seen on the selected block, and what the keyboard picks the block up by. */
export const gripStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'stretch',
  px: 0.25,
  borderRadius: 0.5,
  color: 'text.secondary',
  // The drag context makes the grip a `role="button"` too (see `blockGrabStyles`).
  '&&': { cursor: 'grab' },
  '&:hover': { color: 'text.primary' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light' },
};

export const toolbarNameStyles = {
  minWidth: 0,
  px: 0.5,
  gap: 0.5,
  alignItems: 'center',
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
};

/** The foot of a page's blocks: nothing to see, a place for the keyboard to carry a block to. */
export const footStyles = { height: 0 };

/** A page with nothing on it says so, within its margins. A drop lights it up. */
export const emptyPageStyles = {
  position: 'absolute',
  inset: `${pt(freePage.paddingY)} ${pt(freePage.paddingX)}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  px: 2,
  borderRadius: 1,
  border: '1px dashed',
  fontSize: 13,
  lineHeight: 1.4,
  textAlign: 'center',
  // The page under it takes the click and the drop.
  pointerEvents: 'none',
};

export const emptyPageLitStyles = {
  borderStyle: 'solid',
  borderColor: 'primary.main',
  backgroundColor: accent(0.1),
};

/** Under a sheet: that the page holds more than fits on it. */
export const overflowNoteStyles = {
  display: 'block',
  color: 'text.secondary',
};

/** Where a section's next page would go: after its last sheet, or in its place while it has none. */
export const addPageStyles = {
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 1,
  px: 1.5,
  py: 1,
  borderRadius: 1,
  border: '1px dashed',
  borderColor: 'divider',
  color: 'text.secondary',
};

/**
 * The place of the results while the report prints no page of them: not paper, which would
 * be taken for a page, but a dashed place like the one a section's next page would go to.
 * The lines its two sections are selected by stand in it one under the other, with room for
 * a part's outline between them.
 */
export const noResultsStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
  px: 1.5,
  py: 1,
  borderRadius: 1,
  border: '1px dashed',
  borderColor: 'divider',
  color: 'text.secondary',
  typography: 'body2',
  fontStyle: 'italic',
};

/** The part of a results page that flows, kept clear of the footer, with room for a part's outline. */
export const flowClipStyles = {
  ...clipStyles,
  height: 'calc(100% + 16px)',
  margin: '-8px',
  padding: '8px',
};

/**
 * A part of the report that is set up and not composed: a strip, the summary, the checkpoints.
 * It is a button to its settings, and looks like the page it is on.
 */
export const regionStyles = {
  // A column, as every box of the renderer is: what is in it keeps its margins to itself.
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  width: '100%',
  textAlign: 'left',
  borderRadius: '2px',
  outline: '1px dashed transparent',
  outlineOffset: 3,
  userSelect: 'none',
  '&:hover': { outlineColor: accent(0.55) },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.light' },
};

export const regionSelectedStyles = {
  outline: '2px solid',
  outlineColor: 'primary.main',
  '&:hover': { outlineColor: 'primary.main' },
};

/** Where the renderer puts the two strips on a page (`header` and `footer` of `DesignReport.tsx`). */
export const stripPlaceStyles = {
  header: { position: 'absolute', zIndex: 1, top: pt(24), left: pt(42), right: pt(42), width: 'auto' },
  footer: { position: 'absolute', zIndex: 1, bottom: pt(24), left: pt(42), right: pt(42), width: 'auto' },
};

/**
 * The overlay a dragged thing rides in lets everything through to what is under it: it lies
 * under the pointer for the whole of a drag, and taking the wheel itself it kept the canvas
 * from being scrolled with something in hand.
 */
export const overlayStyle = { pointerEvents: 'none' } as const;

/** The pointer while something is carried, whatever it is over: the overlay no longer says it. */
export const carryingStyles = { '*': { cursor: 'grabbing !important' } };

/** A block in the overlay, under the pointer: the block itself, lifted off its page. */
export const ghostStyles = {
  ...typeStyles,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  borderRadius: '2px',
  backgroundColor: alpha(palette.paper, 0.94),
  boxShadow: '0 8px 22px rgba(0, 0, 0, .4)',
};

export const navyGhostStyles = { backgroundColor: alpha(palette.navy, 0.94) };

// ---- The palette ----

/**
 * The palette, set off from the sections list under it by a rule with room on both sides.
 * One is what can be added and the other what the design holds, and standing close the
 * last entries of the first read as the start of the second.
 */
export const paletteStyles = {
  mb: 2,
  pb: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

export const paletteGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 0.75,
};

/** A block type: a button that adds it, and a thing to drag onto a page. */
export const paletteEntryStyles = {
  justifyContent: 'flex-start',
  gap: 1,
  minHeight: 36,
  px: 1,
  py: 0.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.25,
  textAlign: 'left',
  touchAction: 'manipulation',
  '& i': { flexShrink: 0, fontSize: 17, color: 'text.secondary' },
  '&:hover': { backgroundColor: 'action.hover', borderColor: accent(0.55) },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.light' },
};

// As `blockGrabStyles`, and said once more: an entry is a real button, and Bootstrap's
// `[type="button"]:not(:disabled)` weighs as much as two classes.
export const paletteGrabStyles = { '&&&': { cursor: 'grab' } };

/** A palette entry in the overlay, under the pointer. */
export const paletteGhostStyles = {
  ...paletteEntryStyles,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  backgroundColor: 'background.paper',
  borderColor: 'primary.main',
  boxShadow: '0 8px 22px rgba(0, 0, 0, .4)',
};
